import React, { createContext, useContext, useState } from "react";

import generateRandomId from "../../helpers/generateRandomId";

import Toast from "../../components/Toast/Toast";

export type ToastModel = Omit<React.ComponentProps<typeof Toast>, "onCancel">;
export type ToastStatus = ToastModel["status"];

/**
 * Create the Contexts to be used for the Toast Provider.
 */
const ToastStateContext = createContext<unknown>({});
const ToastUpdaterContext = createContext<unknown>({});

/**
 * The Toast Provider.
 * This is used to wrap the entire application so that any component can
 * access the toast state and updater.
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */

const ToastProvider = ({ children }: { children: React.ReactNode }) => {
    const [toastState, setToastState] = useState<ToastModel[]>([]);

    return (
        <ToastStateContext.Provider value={toastState}>
            <ToastUpdaterContext.Provider value={setToastState}>{children}</ToastUpdaterContext.Provider>
        </ToastStateContext.Provider>
    );
};

/**
 * Hook for pulling through the toast state.
 * This is separate to the updater as some components will need to
 * read the state but not update it.
 */
const useToastState = () => {
    const toastState = useContext(ToastStateContext) as ToastModel[];
    if (typeof toastState === "undefined") {
        throw new Error("useToastState must be used within a ToastProvider");
    }
    return toastState;
};

/**
 * Hook for updating the toast state.
 * This is separate to the state as some components will need to
 * update the state but not read it.
 * @returns {addToast, removeToast}
 */
function useToastUpdater() {
    const setToastState = useContext(ToastUpdaterContext) as React.Dispatch<React.SetStateAction<ToastModel[]>>;

    if (typeof setToastState === "undefined") {
        throw new Error("useToastUpdater must be used within a ToastProvider");
    }

    const addToast = (title: ToastModel["title"], status: ToastModel["status"], href: ToastModel["href"]) => {
        setToastState((state: ToastModel[]) => {
            const newToast: ToastModel = {
                id: generateRandomId(),
                title,
                status,
                href,
            };

            return [...state, newToast];
        });
    };

    const removeToast = (id: ToastModel["id"]) => {
        setToastState((state: ToastModel[]) => state.filter((toast) => toast.id !== id));
    };

    return { addToast, removeToast };
}

export { ToastProvider, useToastState, useToastUpdater };
